export interface StoredTokenMeta {
  tokenType: string;
  accessExpiresIn: string;
  refreshExpiresIn: string;
}

export interface AuthSessionSnapshot {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  accessExpiresIn: string;
  refreshExpiresIn: string;
  user?: unknown;
}

export type AuthSyncAction = 'request' | 'state' | 'logout';

export interface AuthSyncMessage {
  id: string;
  action: AuthSyncAction;
  sourceId: string;
  createdAt: number;
  snapshot?: AuthSessionSnapshot;
}

const ACCESS_TOKEN_STORAGE_KEY = 'wanxiang_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'wanxiang_refresh_token';
const TOKEN_META_STORAGE_KEY = 'wanxiang_token_meta';
const AUTH_USER_STORAGE_KEY = 'wanxiang_auth_user';
const AUTH_SYNC_STORAGE_KEY = 'wanxiang_auth_sync_message';
const AUTH_SYNC_CHANNEL_NAME = 'wanxiang_auth_sync';

const LEGACY_KEYS = [
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  TOKEN_META_STORAGE_KEY,
  AUTH_USER_STORAGE_KEY,
] as const;

let legacyMigrated = false;
let transportInitialized = false;
let authBroadcastChannel: BroadcastChannel | null = null;
const authSyncListeners = new Set<(message: AuthSyncMessage) => void>();
const runtimeTabId = hasWindow() && typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function createMessageId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function migrateLegacyAuthStorage() {
  if (!hasWindow() || legacyMigrated) return;
  legacyMigrated = true;

  const hasSessionAuth = LEGACY_KEYS.some((key) => window.sessionStorage.getItem(key) !== null);
  if (hasSessionAuth) return;

  for (const key of LEGACY_KEYS) {
    const legacyValue = window.localStorage.getItem(key);
    if (legacyValue !== null) {
      window.sessionStorage.setItem(key, legacyValue);
      window.localStorage.removeItem(key);
    }
  }
}

function readAuthSessionSnapshot(): AuthSessionSnapshot | null {
  const accessToken = getSessionItem(ACCESS_TOKEN_STORAGE_KEY);
  const refreshToken = getSessionItem(REFRESH_TOKEN_STORAGE_KEY);
  const tokenMeta = getStoredTokenMeta();
  const user = getStoredAuthUser();
  if (!accessToken || !refreshToken || !tokenMeta || !user) {
    return null;
  }
  return {
    accessToken,
    refreshToken,
    tokenType: tokenMeta.tokenType,
    accessExpiresIn: tokenMeta.accessExpiresIn,
    refreshExpiresIn: tokenMeta.refreshExpiresIn,
    user,
  };
}

function writeAuthSessionSnapshot(snapshot: AuthSessionSnapshot) {
  setSessionItem(ACCESS_TOKEN_STORAGE_KEY, snapshot.accessToken);
  setSessionItem(REFRESH_TOKEN_STORAGE_KEY, snapshot.refreshToken);
  setSessionItem(
    TOKEN_META_STORAGE_KEY,
    JSON.stringify({
      tokenType: snapshot.tokenType,
      accessExpiresIn: snapshot.accessExpiresIn,
      refreshExpiresIn: snapshot.refreshExpiresIn,
    }),
  );
  if (snapshot.user !== undefined) {
    storeAuthUser(snapshot.user);
  }
}

function clearSessionOnly() {
  if (!hasWindow()) return;
  for (const key of LEGACY_KEYS) {
    window.sessionStorage.removeItem(key);
  }
}

function notifyAuthSyncListeners(message: AuthSyncMessage) {
  for (const listener of authSyncListeners) {
    try {
      listener(message);
    } catch {
      // Ignore individual listener failures.
    }
  }
}

function handleIncomingAuthSyncMessage(message: AuthSyncMessage | null) {
  if (!message || typeof message !== 'object') return;
  if (message.sourceId === runtimeTabId) return;

  if (message.action === 'request') {
    const snapshot = readAuthSessionSnapshot();
    if (snapshot) {
      postAuthSyncMessage({
        id: createMessageId(),
        action: 'state',
        sourceId: runtimeTabId,
        createdAt: Date.now(),
        snapshot,
      });
    }
    return;
  }

  if (message.action === 'state' && message.snapshot) {
    writeAuthSessionSnapshot(message.snapshot);
    notifyAuthSyncListeners(message);
    return;
  }

  if (message.action === 'logout') {
    clearSessionOnly();
    notifyAuthSyncListeners(message);
  }
}

function ensureAuthSyncTransport() {
  if (!hasWindow() || transportInitialized) return;
  transportInitialized = true;

  if (typeof BroadcastChannel !== 'undefined') {
    authBroadcastChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
    authBroadcastChannel.onmessage = (event) => {
      handleIncomingAuthSyncMessage(event.data as AuthSyncMessage | null);
    };
    return;
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_SYNC_STORAGE_KEY || !event.newValue) return;
    try {
      handleIncomingAuthSyncMessage(JSON.parse(event.newValue) as AuthSyncMessage);
    } catch {
      // Ignore malformed sync payloads.
    }
  });
}

function postAuthSyncMessage(message: AuthSyncMessage) {
  if (!hasWindow()) return;
  ensureAuthSyncTransport();

  if (authBroadcastChannel) {
    authBroadcastChannel.postMessage(message);
    return;
  }

  window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, JSON.stringify(message));
  window.localStorage.removeItem(AUTH_SYNC_STORAGE_KEY);
}

function getSessionItem(key: string): string | null {
  if (!hasWindow()) return null;
  migrateLegacyAuthStorage();
  return window.sessionStorage.getItem(key);
}

function setSessionItem(key: string, value: string) {
  if (!hasWindow()) return;
  window.sessionStorage.setItem(key, value);
}

function removeAuthKey(key: string) {
  if (!hasWindow()) return;
  window.sessionStorage.removeItem(key);
  window.localStorage.removeItem(key);
}

export function getAccessToken(): string | null {
  return getSessionItem(ACCESS_TOKEN_STORAGE_KEY);
}

export function setAccessToken(token: string) {
  setSessionItem(ACCESS_TOKEN_STORAGE_KEY, token);
}

export function clearAccessToken() {
  removeAuthKey(ACCESS_TOKEN_STORAGE_KEY);
}

export function getRefreshToken(): string | null {
  return getSessionItem(REFRESH_TOKEN_STORAGE_KEY);
}

export function setRefreshToken(token: string) {
  setSessionItem(REFRESH_TOKEN_STORAGE_KEY, token);
}

export function getStoredTokenMeta(): StoredTokenMeta | null {
  const raw = getSessionItem(TOKEN_META_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokenMeta;
  } catch {
    return null;
  }
}

export function setStoredTokenMeta(meta: StoredTokenMeta) {
  setSessionItem(TOKEN_META_STORAGE_KEY, JSON.stringify(meta));
}

export function clearStoredTokenMeta() {
  removeAuthKey(TOKEN_META_STORAGE_KEY);
}

export function getStoredAuthUser<T = unknown>(): T | null {
  const raw = getSessionItem(AUTH_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function storeAuthUser(user: unknown) {
  setSessionItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
}

export function clearStoredAuthUser() {
  removeAuthKey(AUTH_USER_STORAGE_KEY);
}

export function clearAuthSession(options: { broadcast?: boolean } = {}) {
  if (!hasWindow()) return;
  clearSessionOnly();
  window.localStorage.removeItem(AUTH_SYNC_STORAGE_KEY);
  if (options.broadcast !== false) {
    postAuthSyncMessage({
      id: createMessageId(),
      action: 'logout',
      sourceId: runtimeTabId,
      createdAt: Date.now(),
    });
  }
}

export function persistAuthSession(
  payload: AuthSessionSnapshot,
  options: { broadcast?: boolean } = {},
) {
  writeAuthSessionSnapshot(payload);
  if (options.broadcast !== false) {
    postAuthSyncMessage({
      id: createMessageId(),
      action: 'state',
      sourceId: runtimeTabId,
      createdAt: Date.now(),
      snapshot: payload,
    });
  }
}

export function broadcastCurrentAuthState() {
  const snapshot = readAuthSessionSnapshot();
  if (!snapshot) return;
  postAuthSyncMessage({
    id: createMessageId(),
    action: 'state',
    sourceId: runtimeTabId,
    createdAt: Date.now(),
    snapshot,
  });
}

export function subscribeAuthSync(listener: (message: AuthSyncMessage) => void) {
  ensureAuthSyncTransport();
  authSyncListeners.add(listener);
  return () => {
    authSyncListeners.delete(listener);
  };
}

export function requestAuthSessionSync() {
  postAuthSyncMessage({
    id: createMessageId(),
    action: 'request',
    sourceId: runtimeTabId,
    createdAt: Date.now(),
  });
}
